import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ResearchPipelinePresenceMatrix = ({ project, codes, title }) => {
    const { t } = useTranslation();
    const [headerBgColor] = useState("#ffffff");
    const [headerTextColor] = useState("#000000");

    // Extract files from research pipeline structure (analysis_parents)
    const files = useMemo(() => {
        const analysisArray = project?.analysis_parents || [];
        return analysisArray.map(a => ({
            id: a.id,
            name: a.name || a.filename || a.uuid || a.id,
            uuid: a.uuid,
        }));
    }, [project]);

    // Flatten codes and subcodes into rows
    const getAllRows = (codes) => {
        let rows = [];
        codes.forEach(code => {
            if (!code.parent) {
                rows.push(code);
            }
            // Add subcodes
            const subcodes = codes.filter(sub => sub.parent === code.uuid);
            rows.push(...subcodes);
        });
        return rows;
    };

    const rows = useMemo(() => getAllRows(codes), [codes]);

    // Build presence matrix: code id x file id -> count
    const presenceMatrix = useMemo(() => {
        const matrix = {};
        
        rows.forEach(code => {
            const counts = {};
            files.forEach(file => {
                // Match excerpts to files using analysis_uuid (enriched by PipelineResults)
                const matchCount = (code.excerpts || []).filter(excerpt => 
                    excerpt.analysis_uuid === file.uuid
                ).length;
                counts[file.id] = matchCount;
            });
            matrix[code.id || code.uuid] = counts;
        });
        
        return matrix;
    }, [rows, files]);

    // Style for table headers
    const headerStyle = {
        backgroundColor: headerBgColor,
        color: headerTextColor,
        verticalAlign: "middle",
    };

    if (!files.length || !rows.length) {
        return (
            <div className="text-center py-8 text-gray-500">
                <p>No data available for presence matrix</p>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200">
                            Code
                        </th>
                        {files.map(file => (
                            <th key={file.id} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200 whitespace-nowrap min-w-[150px]">
                                {file.name}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {rows.map(code => {
                        const codeCounts = presenceMatrix[code.id || code.uuid] || {};
                        const totalExcerpts = Object.values(codeCounts).reduce((sum, count) => sum + count, 0);
                        
                        return (
                            <tr key={code.id || code.uuid} className="hover:bg-gray-50">
                                <td className="px-4 py-3 border-b border-r border-gray-200">
                                    <div className="flex items-center gap-2">
                                        <div 
                                            className="w-3 h-3 rounded border border-gray-300 flex-shrink-0"
                                            style={{ backgroundColor: code.color || '#gray' }}
                                        ></div>
                                        <span className="font-medium text-gray-900">{code.name}</span>
                                        <span className="text-sm text-gray-500">({totalExcerpts})</span>
                                    </div>
                                </td>
                                {files.map(file => {
                                    const count = codeCounts[file.id] || 0;
                                    return (
                                        <td key={file.id} className="px-4 py-3 border-b border-r border-gray-200 text-center">
                                            {count > 0 ? (
                                                <div className="flex items-center justify-center">
                                                    <span className="text-green-600 text-lg font-bold">
                                                        ✓
                                                    </span>
                                                    <span className="ml-1 text-sm text-gray-600">({count})</span>
                                                </div>
                                            ) : (
                                                <span className="text-red-600 text-lg font-bold">
                                                    ✗
                                                </span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default ResearchPipelinePresenceMatrix;